// Turns a report's branch selection into the X-Branch-Id the backend already understands: "all"
// for every accessible branch, "none" for a filter the operator emptied, a single id, or a
// comma-separated list for a multi-branch view. Each id is re-checked against the caller's own
// access server-side, so a selection can narrow what a user sees but never widen it.
export const ALL_BRANCHES = 'all';

// An emptied selection has to be said out loud. Sending no header instead is indistinguishable
// from not having a scope, which the interceptor fills in with the pinned branch — the report
// would then show a branch while its filter claimed none.
export const NO_BRANCHES = 'none';

export const branchScopeHeader = (selection) => {
  if (selection === ALL_BRANCHES) return ALL_BRANCHES;
  if (Array.isArray(selection)) {
    const ids = selection.filter((id) => id !== null && id !== undefined && id !== '');
    // A one-item list is a single-branch view; the backend treats the two the same.
    return ids.length ? ids.map(String).join(',') : NO_BRANCHES;
  }
  return selection ? String(selection) : null;
};

// Holding every branch the club has carries the same meaning as being unrestricted, so it collapses
// to the sentinel and keeps the "all branches" tick honest. An empty list stays empty: it is a
// scope of no branches, which branchScopeHeader can now express.
export const normalizeBranchSelection = (ids, totalBranches) => {
  const list = ids || [];
  return list.length && list.length === totalBranches ? ALL_BRANCHES : list;
};

export const toggleAllBranchSelection = (selection) => (
  selection === ALL_BRANCHES ? [] : ALL_BRANCHES
);

export const toggleBranchSelection = (selection, branchId, availableBranchIds) => {
  const current = selection === ALL_BRANCHES
    ? availableBranchIds.map(String)
    : (selection || []).map(String);
  const key = String(branchId);
  const next = current.includes(key)
    ? current.filter((id) => id !== key)
    : [...current, key];

  return normalizeBranchSelection(next, availableBranchIds.length);
};

// Returns a request config for `api.get(path, params, config)`. Null selection means "leave it to
// the global switcher" — the interceptor then falls back to the pinned branch as before.
export const branchScopeConfig = (selection) => {
  const header = branchScopeHeader(selection);
  return header ? { headers: { 'X-Branch-Id': header } } : {};
};

// Whether a branch set is worth listing, or is just "everywhere" said the long way. Holding every
// branch the club has carries the same meaning as being unrestricted, so both read as one phrase
// rather than a row of chips the reader has to count.
//
// `unrestrictedWhenEmpty` is the difference between the two grids: a staff user with no branch rows
// reaches every branch, while a member with none has simply not been placed in one yet.
export const reachesAllBranches = (names, totalBranches, unrestrictedWhenEmpty = false) => {
  const held = (names || []).length;
  if (!held) return unrestrictedWhenEmpty;
  return totalBranches > 0 && held >= totalBranches;
};
